import { Fragment, useMemo } from "react";
import {
  Box,
  Checkbox,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from "@mui/material";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import { numberFormatter } from "../../helper/Reports/common";
import SwitchBtn from "../../components/Buttons/switchBtn";
import {
  isCampaignTypeSP,
  SB_SD_OPTIMISATION_TOOLTIP,
} from "./targetingV2LocalFilters";

const HEADER_MAX_LENGTH = 22;
/** Fixed checkbox column width — must match sticky `left` offsets exactly. */
const CHECKBOX_COL_WIDTH = 52;

const STRING_COLUMN_KEYS = [
  "portfolio_name",
  "product_set",
  "targeting_type",
  "match_type",
  "campaign_type",
  "campaign_format",
  "campaign_bid_strategy",
  "status",
];

function getTruncatedHeaderText(text = "") {
  const value = String(text);
  if (value.length <= HEADER_MAX_LENGTH) return value;
  return `${value.slice(0, HEADER_MAX_LENGTH - 1)}…`;
}

function isColumnSortable(column) {
  return column?.type === "number";
}

/** Sticky left offset for each sticky column, after the checkbox column. */
function buildStickyOffsetMap(columns = []) {
  const map = {};
  let left = CHECKBOX_COL_WIDTH;
  (columns || []).forEach((column) => {
    if (!column?.isSticky) return;
    map[column.key] = left;
    left += column.minWidth || 120;
  });
  return map;
}

function getFormattedCompChangePercent(value) {
  const formattedObj = {
    printableValue: "-",
    numericValue: value,
  };
  if (value === undefined || value === null || isNaN(value)) {
    return formattedObj;
  }
  if (value === 2147483647 || value === -2147483648) {
    formattedObj.printableValue = "∞";
    return formattedObj;
  }
  formattedObj.printableValue = `(${Number(value).toFixed(0)}%)`;
  return formattedObj;
}

export default function TargetingV2Table({
  columns = [],
  rows = [],
  isLoading = false,
  queryOrder = [["spends", "desc"]],
  onSortClick,
  summaryData = {},
  onToggleRow,
  onToggleAll,
  isComparisonView = false,
  onToggleStatus,
  noDataFoundMessage = "No Data Found",
}) {
  const sortKey = queryOrder?.[0]?.[0];
  const sortDir = queryOrder?.[0]?.[1] || "desc";
  const stickyOffsetByKey = useMemo(() => buildStickyOffsetMap(columns), [columns]);
  const selectableRows = useMemo(
    () => (rows || []).filter((row) => isCampaignTypeSP(row?.campaign_type)),
    [rows]
  );
  const allSelectableChecked =
    selectableRows.length > 0 && selectableRows.every((row) => row?.isChecked);

  if (!isLoading && (!rows || rows.length === 0)) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: 250,
          color: "text.secondary",
          gap: 1.5,
          border: "1px solid #ddd",
          borderRadius: "8px",
          mt: 2,
        }}
      >
        <SearchOffIcon sx={{ fontSize: 48, color: "grey.500" }} />
        <Typography variant="subtitle1">{noDataFoundMessage}</Typography>
      </Box>
    );
  }

  const stickyLeftStyle = (left = 0, bgColor = "#ffffff") => ({
    position: "sticky",
    left,
    zIndex: 3,
    background: bgColor,
    backgroundClip: "padding-box",
    boxSizing: "border-box",
    borderRight: "1px solid #ddd",
    boxShadow: "1px 0 0 0 #ddd",
  });

  const checkboxCellStyle = (bgColor = "#ffffff", extra = {}) => ({
    ...stickyLeftStyle(0, bgColor),
    width: CHECKBOX_COL_WIDTH,
    minWidth: CHECKBOX_COL_WIDTH,
    maxWidth: CHECKBOX_COL_WIDTH,
    padding: "0 !important",
    textAlign: "center",
    ...extra,
  });

  const bodyCellBase = {
    fontSize: "0.75rem",
    padding: "14px 16px",
    borderRight: "1px solid #ddd",
    borderBottom: "1px solid #eee",
    boxSizing: "border-box",
  };

  const getStickyLeft = (column) => stickyOffsetByKey[column?.key] ?? CHECKBOX_COL_WIDTH;

  // Row heights that drive the sticky `top` offsets for header rows below.
  const TITLE_ROW_HEIGHT = 56;
  const COMPARISON_LABEL_ROW_HEIGHT = 40;
  const summaryRowTop = TITLE_ROW_HEIGHT + (isComparisonView ? COMPARISON_LABEL_ROW_HEIGHT : 0);

  const renderDimensionCell = (column, row) => {
    const { productName, asin } = column?.innerKeys || {};
    if (productName) {
      const name = row?.[productName] ?? row?.[column.key];
      const asinValue = asin?.value ? row?.[asin.value] : null;
      const displayName = name ?? "-";
      return (
        <Box sx={{ minWidth: 0 }}>
          <Tooltip title={displayName} arrow placement="top">
            <Typography
              sx={{
                fontSize: "12px",
                color: "#333",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: column.minWidth || 220,
              }}
            >
              {displayName}
            </Typography>
          </Tooltip>
          {asinValue ? (
            <Typography sx={{ fontSize: "11px", color: "text.secondary" }}>
              {asin?.label ? `${asin.label}: ` : ""}
              {asinValue}
            </Typography>
          ) : null}
        </Box>
      );
    }

    const isTargetingCol = column?.key === "targeting";
    const value = row?.[column.key];
    if (value === undefined || value === null || value === "") return "-";
    return (
      <Tooltip title={String(value)} arrow placement="top">
        <Typography
          sx={{
            fontSize: "12px",
            color: "#333",
            overflow: "hidden",
            textOverflow: isTargetingCol ? "unset" : "ellipsis",
            whiteSpace: isTargetingCol ? "normal" : "nowrap",
            wordBreak: isTargetingCol ? "break-word" : "normal",
            maxWidth: column.minWidth || 160,
          }}
        >
          {String(value)}
        </Typography>
      </Tooltip>
    );
  };

  const renderStringCell = (column, row) => {
    const value = row?.[column.key];
    if (value === undefined || value === null || value === "") return "-";
    return (
      <Tooltip title={String(value)} arrow placement="top">
        <Typography
          sx={{
            fontSize: "12px",
            color: "#333",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            maxWidth: column.minWidth || 160,
          }}
        >
          {String(value)}
        </Typography>
      </Tooltip>
    );
  };

  const renderBidCell = (row) => {
    const value = row?.bid;
    if (value === undefined || value === null || value === "") return "-";
    return numberFormatter(value, true, undefined, 2);
  };

  const renderStatusCell = (row) => {
    const normalizedStatus = String(row?.status ?? "").toUpperCase();
    const isEnabled = normalizedStatus === "ENABLED";
    const isSpCampaign = isCampaignTypeSP(row?.campaign_type);
    // Same as normal view: SP only (comparison view no longer disables the switch).
    const isSwitchDisabled = !isSpCampaign;

    const switchControl = (
      <SwitchBtn
        checked={isEnabled}
        disabled={isSwitchDisabled}
        onChange={(e) => {
          e.stopPropagation();
          if (isSwitchDisabled) return;
          onToggleStatus?.(row);
        }}
      />
    );

    // Keep centering container outermost so Tooltip wrapper doesn't left-align disabled switches.
    return (
      <div
        className="child-switch-btn-container"
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        {isSpCampaign ? (
          switchControl
        ) : (
          <Tooltip title={SB_SD_OPTIMISATION_TOOLTIP} arrow placement="top">
            <span style={{ display: "inline-flex" }}>{switchControl}</span>
          </Tooltip>
        )}
      </div>
    );
  };

  const renderComparisonNumberCell = (column, row) => {
    const value = row?.current_data?.[column.key];
    const changePercent = getFormattedCompChangePercent(row?.change_percentage_data?.[column.key]);
    if (value === undefined || value === null || value === "") return "-";
    return (
      <Box display="flex" flexDirection="column" alignItems={column.alignment === "left" ? "flex-start" : "flex-end"}>
        <Typography sx={{ fontSize: "0.75rem", fontWeight: 500 }}>
          {numberFormatter(value, !!column?.isCurrencyFormat, column?.format, column?.maximumFractionDigits)}
        </Typography>
        {changePercent?.printableValue && changePercent.printableValue !== "-" ? (
          <Typography
            sx={{
              fontSize: "0.75rem",
              color: changePercent.numericValue < 0 ? "#d32f2f" : "#388e3c",
              lineHeight: 1.1,
            }}
          >
            {changePercent.printableValue}
          </Typography>
        ) : null}
      </Box>
    );
  };

  const renderComparisonPreviousCell = (column, row) => {
    const value = row?.comparison_data?.[column.key];
    if (value === undefined || value === null || value === "") return "-";
    return (
      <Typography sx={{ fontSize: "0.75rem", fontWeight: 500 }}>
        {numberFormatter(value, !!column?.isCurrencyFormat, column?.format, column?.maximumFractionDigits)}
      </Typography>
    );
  };

  const renderNumberCell = (column, row) => {
    const value = row?.[column.key];
    if (value === undefined || value === null || value === "") return "-";
    return numberFormatter(value, !!column?.isCurrencyFormat, column?.format, column?.maximumFractionDigits);
  };

  const renderCellValue = (column, row, index) => {
    if (column.key === "bid") return renderBidCell(row);
    if (column.key === "status") return renderStatusCell(row);
    if (column?.innerKeys) return renderDimensionCell(column, row);
    if (STRING_COLUMN_KEYS.includes(column.key)) return renderStringCell(column, row);
    if (isComparisonView && column.colSpan === 2) return renderComparisonNumberCell(column, row);
    return renderNumberCell(column, row);
  };

  const renderSummaryValue = (column) => {
    if (column?.isDimension || column?.innerKeys || STRING_COLUMN_KEYS.includes(column.key) || column.key === "bid") {
      return "-";
    }
    if (isComparisonView && column.colSpan === 2) {
      const value = summaryData?.current_data?.[column.key];
      const changePercent = getFormattedCompChangePercent(summaryData?.change_percentage_data?.[column.key]);
      if (value === undefined || value === null || value === "") return "-";
      return (
        <Box display="flex" flexDirection="column" alignItems={column.alignment === "left" ? "flex-start" : "flex-end"}>
          <Typography sx={{ fontSize: "0.75rem", fontWeight: 600 }}>
            {numberFormatter(value, !!column?.isCurrencyFormat, column?.format, column?.maximumFractionDigits)}
          </Typography>
          {changePercent?.printableValue && changePercent.printableValue !== "-" ? (
            <Typography
              sx={{
                fontSize: "0.75rem",
                fontWeight: 600,
                color: changePercent.numericValue < 0 ? "#d32f2f" : "#388e3c",
                lineHeight: 1.1,
              }}
            >
              {changePercent.printableValue}
            </Typography>
          ) : null}
        </Box>
      );
    }
    const value = summaryData?.[column.key] ?? summaryData?.current_data?.[column.key];
    if (value === undefined || value === null || value === "") return "-";
    return numberFormatter(value, !!column?.isCurrencyFormat, column?.format, column?.maximumFractionDigits);
  };

  const renderSummaryPreviousValue = (column) => {
    const value = summaryData?.comparison_data?.[column.key];
    if (value === undefined || value === null || value === "") return "-";
    return numberFormatter(value, !!column?.isCurrencyFormat, column?.format, column?.maximumFractionDigits);
  };

  const allChecked = allSelectableChecked;

  const selectStylesForHeader = (isSticky, stickyLeft, isActive) => ({
    ...(isSticky
      ? stickyLeftStyle(stickyLeft, isActive ? "#F7F6FE" : "#fff")
      : {
          background: isActive ? "#F7F6FE" : "#fff",
          borderRight: "1px solid #ddd",
        }),
    position: "sticky",
    top: 0,
    zIndex: isSticky ? 4 : 2,
    color: "#00000099",
    fontWeight: 600,
    fontSize: "0.75rem",
    whiteSpace: "nowrap",
    borderBottom: "none",
    padding: "14px 16px",
    boxSizing: "border-box",
  });

  return (
    <Paper
      sx={{
        width: "100%",
        overflow: "auto",
        border: "1px solid #ddd",
        borderRadius: "8px",
        boxShadow: "none",
        mt: 2,
      }}
    >
      <TableContainer sx={{ maxHeight: 600, minHeight: 400 }}>
        <Table stickyHeader size="small" sx={{ borderCollapse: "separate" }}>
          <TableHead>
            <TableRow sx={{ height: TITLE_ROW_HEIGHT }}>
              <TableCell
                sx={checkboxCellStyle("#fff", {
                  zIndex: 4,
                  top: 0,
                  borderBottom: "none",
                })}
              >
                <Checkbox
                  size="small"
                  checked={allChecked}
                  indeterminate={
                    selectableRows.some((row) => row?.isChecked) && !allChecked
                  }
                  onChange={(e) => onToggleAll?.(e.target.checked)}
                  sx={{ p: 0.5 }}
                />
              </TableCell>
              {columns.map((column) => {
                const isSticky = !!column?.isSticky;
                const stickyLeft = getStickyLeft(column);
                const colWidth = column.minWidth || 120;
                const sortable = isColumnSortable(column);
                const colSpan = isComparisonView && column.colSpan === 2 ? 2 : 1;
                return (
                  <TableCell
                    key={`${column.key}-header`}
                    colSpan={colSpan}
                    align={column.alignment || (column.isDimension ? "left" : "right")}
                    sx={{
                      ...selectStylesForHeader(isSticky, stickyLeft, sortKey === column.key),
                      // Keep per-column minWidth; colSpan owns the double width (ProductAnalytics pattern).
                      minWidth: colWidth,
                      width: colWidth,
                      maxWidth: isSticky ? colWidth : undefined,
                      cursor: sortable ? "pointer" : "default",
                    }}
                    onClick={() => {
                      if (!sortable) return;
                      onSortClick?.(column.key, sortKey === column.key && sortDir === "asc" ? "desc" : "asc");
                    }}
                  >
                    {sortable ? (
                      <TableSortLabel
                        active={sortKey === column.key}
                        direction={sortKey === column.key ? sortDir : "asc"}
                        sx={{
                          "& .MuiTableSortLabel-icon": {
                            color: "#00000099 !important",
                          },
                        }}
                      >
                        <Tooltip title={column.shortTitle || ""} arrow placement="top">
                          <Typography component="span" sx={{ fontSize: "0.75rem", fontWeight: 600, color: "#00000099" }}>
                            {getTruncatedHeaderText(column.shortTitle)}
                          </Typography>
                        </Tooltip>
                      </TableSortLabel>
                    ) : (
                      <Tooltip title={column.shortTitle || ""} arrow placement="top">
                        <Typography component="span" sx={{ fontSize: "0.75rem", fontWeight: 600, color: "#00000099" }}>
                          {getTruncatedHeaderText(column.shortTitle)}
                        </Typography>
                      </Tooltip>
                    )}
                  </TableCell>
                );
              })}
            </TableRow>

            {isComparisonView && (
              <TableRow
                sx={{
                  position: "sticky",
                  top: TITLE_ROW_HEIGHT,
                  zIndex: 3,
                  backgroundColor: "#f9f9f9",
                  height: COMPARISON_LABEL_ROW_HEIGHT,
                }}
              >
                <TableCell
                  sx={{
                    ...checkboxCellStyle("#f9f9f9", {
                      zIndex: 5,
                      top: TITLE_ROW_HEIGHT,
                      borderTop: "1px solid #ddd",
                    }),
                  }}
                />
                {columns.map((column) => {
                  const isSticky = !!column?.isSticky;
                  const stickyLeft = getStickyLeft(column);
                  const colWidth = column.minWidth || 120;
                  const isComparisonMetric = column.colSpan === 2;
                  const labelCellSx = {
                    ...(isSticky
                      ? stickyLeftStyle(stickyLeft, "#f9f9f9")
                      : { background: "#f9f9f9", borderRight: "1px solid #ddd" }),
                    position: "sticky",
                    top: TITLE_ROW_HEIGHT,
                    zIndex: isSticky ? 5 : 3,
                    minWidth: colWidth,
                    width: colWidth,
                    maxWidth: isSticky ? colWidth : undefined,
                    borderTop: "1px solid #ddd",
                    padding: "8px 16px",
                    fontWeight: 600,
                    fontSize: "0.75rem",
                    whiteSpace: "nowrap",
                    boxSizing: "border-box",
                  };

                  // Match ProductAnalyticsTable: one physical cell per logical column
                  // (Current + Previous as two cells under a colSpan=2 title).
                  if (!isComparisonMetric) {
                    return (
                      <TableCell
                        key={`comp-label-${column.key}`}
                        align={
                          column.alignment ||
                          (column.isDimension ? "left" : "right")
                        }
                        sx={labelCellSx}
                      >
                        -
                      </TableCell>
                    );
                  }

                  return (
                    <Fragment key={`comp-label-${column.key}`}>
                      <TableCell
                        align={column.alignment || "right"}
                        sx={labelCellSx}
                      >
                        Current
                      </TableCell>
                      <TableCell
                        align={column.alignment || "right"}
                        sx={{
                          ...labelCellSx,
                          ...(isSticky ? {} : { color: "#00000099" }),
                        }}
                      >
                        Previous
                      </TableCell>
                    </Fragment>
                  );
                })}
              </TableRow>
            )}

            <TableRow
              sx={{
                position: "sticky",
                top: summaryRowTop,
                zIndex: 3,
                backgroundColor: "#f9f9f9",
              }}
            >
              <TableCell
                sx={{
                  ...checkboxCellStyle("#f9f9f9", {
                    zIndex: 5,
                    top: summaryRowTop,
                    borderTop: "1px solid #ddd",
                  }),
                }}
              />
              {columns.map((column, colIndex) => {
                const isSticky = !!column?.isSticky;
                const stickyLeft = getStickyLeft(column);
                const colWidth = column.minWidth || 120;
                const isComparisonMetric = isComparisonView && column.colSpan === 2;
                return (
                  <Fragment key={`summary-${column.key}`}>
                    <TableCell
                      align={column.alignment || (column.isDimension ? "left" : "right")}
                      sx={{
                        ...(isSticky
                          ? stickyLeftStyle(stickyLeft, sortKey === column.key ? "#EAE6FD" : "#f9f9f9")
                          : {
                              background: sortKey === column.key ? "#EAE6FD" : "#f9f9f9",
                              borderRight: "1px solid #ddd",
                            }),
                        position: "sticky",
                        top: summaryRowTop,
                        zIndex: isSticky ? 5 : 3,
                        minWidth: colWidth,
                        width: colWidth,
                        maxWidth: isSticky ? colWidth : undefined,
                        borderTop: "1px solid #ddd",
                        padding: "14px 16px",
                        fontWeight: 600,
                        fontSize: "0.75rem",
                        whiteSpace: "nowrap",
                        boxSizing: "border-box",
                      }}
                    >
                      {column.isSticky && colIndex === 0 ? "Summary" : isLoading ? "-" : renderSummaryValue(column)}
                    </TableCell>
                    {isComparisonMetric && (
                      <TableCell
                        align={column.alignment || "right"}
                        sx={{
                          background: "#f9f9f9",
                          borderRight: "1px solid #ddd",
                          position: "sticky",
                          top: summaryRowTop,
                          zIndex: 3,
                          minWidth: colWidth,
                          width: colWidth,
                          borderTop: "1px solid #ddd",
                          padding: "14px 16px",
                          fontWeight: 600,
                          fontSize: "0.75rem",
                          whiteSpace: "nowrap",
                          boxSizing: "border-box",
                        }}
                      >
                        {isLoading ? "-" : renderSummaryPreviousValue(column)}
                      </TableCell>
                    )}
                  </Fragment>
                );
              })}
            </TableRow>
          </TableHead>

          <TableBody>
            {isLoading
              ? Array.from({ length: 8 }).map((_, rowIndex) => (
                  <TableRow key={`skeleton-${rowIndex}`}>
                    <TableCell
                      sx={{
                        ...bodyCellBase,
                        ...checkboxCellStyle("#fff", { zIndex: 2 }),
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%" }}>
                        <Skeleton variant="rectangular" width={18} height={18} />
                      </Box>
                    </TableCell>
                    {columns.map((column) => {
                      const colWidth = column.minWidth || 120;
                      const isComparisonMetric = isComparisonView && column.colSpan === 2;
                      return (
                        <Fragment key={`skeleton-${rowIndex}-${column.key}`}>
                          <TableCell
                            sx={{
                              ...bodyCellBase,
                              ...(column.isSticky ? stickyLeftStyle(getStickyLeft(column)) : {}),
                              minWidth: colWidth,
                              width: colWidth,
                              maxWidth: column.isSticky ? colWidth : undefined,
                            }}
                          >
                            <Skeleton variant="text" width="80%" />
                          </TableCell>
                          {isComparisonMetric && (
                            <TableCell
                              sx={{ ...bodyCellBase, minWidth: colWidth, width: colWidth }}
                            >
                              <Skeleton variant="text" width="80%" />
                            </TableCell>
                          )}
                        </Fragment>
                      );
                    })}
                  </TableRow>
                ))
              : rows.map((row, index) => (
                  <TableRow
                    key={`${row?.targeting}-${row?.campaign_id}-${row?.ad_group_id}-${row?.target_id}-${index}`}
                    hover
                    selected={!!row?.isChecked}
                    sx={{
                      "&:hover td": {
                        backgroundColor: "#f5f5f5",
                      },
                    }}
                  >
                    <TableCell
                      sx={{
                        ...bodyCellBase,
                        ...checkboxCellStyle(row?.isChecked ? "#F7F6FE" : "#fff", { zIndex: 2 }),
                      }}
                    >
                      {(() => {
                        const selectable = isCampaignTypeSP(row?.campaign_type);
                        const checkbox = (
                          <Checkbox
                            size="small"
                            checked={!!row?.isChecked}
                            disabled={!selectable}
                            onChange={() => onToggleRow?.(index)}
                            sx={{ p: 0.5 }}
                          />
                        );
                        if (selectable) return checkbox;
                        return (
                          <Tooltip title={SB_SD_OPTIMISATION_TOOLTIP} arrow placement="top">
                            <span style={{ display: "inline-flex" }}>{checkbox}</span>
                          </Tooltip>
                        );
                      })()}
                    </TableCell>
                    {columns.map((column) => {
                      const colWidth = column.minWidth || 120;
                      const isComparisonMetric = isComparisonView && column.colSpan === 2;
                      return (
                        <Fragment key={`${column.key}-${index}`}>
                          <TableCell
                            align={
                              column.alignment ||
                              (column.isDimension || column.innerKeys || STRING_COLUMN_KEYS.includes(column.key)
                                ? "left"
                                : "right")
                            }
                            sx={{
                              ...bodyCellBase,
                              ...(column.isSticky
                                ? stickyLeftStyle(getStickyLeft(column), row?.isChecked ? "#F7F6FE" : "#fff")
                                : { background: row?.isChecked ? "#F7F6FE" : "#fff" }),
                              minWidth: colWidth,
                              width: colWidth,
                              maxWidth: column.isSticky ? colWidth : undefined,
                              zIndex: column.isSticky ? 2 : 1,
                            }}
                          >
                            {renderCellValue(column, row, index)}
                          </TableCell>
                          {isComparisonMetric && (
                            <TableCell
                              align={column.alignment || "right"}
                              sx={{
                                ...bodyCellBase,
                                background: row?.isChecked ? "#F7F6FE" : "#fff",
                                minWidth: colWidth,
                                width: colWidth,
                              }}
                            >
                              {renderComparisonPreviousCell(column, row)}
                            </TableCell>
                          )}
                        </Fragment>
                      );
                    })}
                  </TableRow>
                ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}
